import { useEffect, useState } from 'react';

import { Sentry } from '@willow/shared-web';
import { PortalLoginConfig } from '@willow/types-iso';

import { fetchLoginConfig, isOnBootOrigin, LoginConfigError, LoginConfigNetworkError } from './portalAuthApi';

export type LoginConfigState = { status: 'loading' } | { status: 'ready'; config: PortalLoginConfig };

// Auth0 is what every Portal is on today and it keeps working through the dual window, so it
// is the safe answer whenever the lookup cannot be trusted.
const AUTH0_MODE: LoginConfigState = { status: 'ready', config: { willowAuthEnabled: false } };

const reportLookupFailure = (error: unknown): void => {
  const status = error instanceof LoginConfigError ? error.status : undefined;
  // A 4xx means the document is somewhere that cannot answer for the Portal, and no response at
  // all is the borrower's connection dropping or an in-app browser moving on mid-request. The hook
  // already handles both. Only a failing server or an unreadable answer is worth paging on.
  const isExpected = (status !== undefined && status < 500) || error instanceof LoginConfigNetworkError;
  Sentry.captureException(error, {
    level: isExpected ? 'warning' : 'error',
    tags: { host: window.location.host, ...(status !== undefined && { status: String(status) }) },
  });
};

export const useLoginConfig = (): LoginConfigState => {
  // Off the boot origin, the relative fetch would resolve against whatever page the tab has
  // moved to, which cannot serve the Portal's API. Nothing to ask and nothing to report.
  const [state, setState] = useState<LoginConfigState>(() => (isOnBootOrigin() ? { status: 'loading' } : AUTH0_MODE));

  useEffect(() => {
    if (!isOnBootOrigin()) return undefined;
    let cancelled = false;

    fetchLoginConfig()
      .then((config) => {
        if (!cancelled) setState({ status: 'ready', config });
      })
      .catch((error: unknown) => {
        // A document that moved between the request and the answer is the same case, and its
        // answer says nothing about the Portal.
        if (isOnBootOrigin()) reportLookupFailure(error);
        if (!cancelled) setState(AUTH0_MODE);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};
